/**
 * Locale registry — the single source of truth for the language axis (FR10).
 *
 * Story 4.1 introduces the load-bearing shape: prefix-all `/:lang/…` URLs map to
 * one of these locale definitions. The *market* (Belgium vs. international) is a
 * property *derived from the locale*, not a separate client-side toggle — that is
 * the seam Stories 4.2–4.9 build on (i18next instances, `useLocale`).
 * 4.1 adds NO `t`, NO i18next, NO SEO signals.
 *
 * NFR10 — this module is **pure data + pure functions only**: zero React /
 * runtime / Node imports. That lets `react-router.config.ts` (Vite/esbuild)
 * import `LIVE_LOCALES` at build time to drive the prerender matrix without
 * pulling in a dependency tree. Keep it that way.
 */

/**
 * CONTENT profile — which editorial angle a locale gets, not which market it is
 * priced in. `"belgium"` means "show the Belgian legislation line": law-2027,
 * CIAO@work, Belgian payroll providers, the BE variants of the FAQ and hero copy.
 * Every one of its ~29 call sites asks exactly that question.
 *
 * Market IDENTITY — the CountrySite that decides the CURRENCY — is a separate,
 * open-ended value resolved from the CMS by `countryFor()` in
 * `content/pricing.ts`. Do NOT derive one from the other: this union has two
 * members by editorial design and cannot name a UK or NZ market, and pricing once
 * did derive from it, which made a market created in the admin invisible to the
 * price layer (DIT-264, ADR-0033). A new market is a CountrySite plus, at most, a
 * `"belgium" | "international"` answer to "does it get the Belgian angle?".
 */
export type Market = "belgium" | "international";

export type LocaleStatus = "live" | "draft";

export interface LocaleDef {
  /** URL prefix segment + i18next language key (e.g. "nl", "en"). */
  code: string;
  /** Value for `<html lang>` (Story 4.5 wires this to the path; 4.1 leaves it hardcoded). */
  htmlLang: string;
  /** Content profile — drives Belgium-only CONTENT. Not the pricing market: see `Market`. */
  market: Market;
  /** "live" = shipped on a prefix; "draft" = staged, not routed/prerendered yet. */
  status: LocaleStatus;
  /** Human-readable name in its own language (for a future language switcher). */
  name: string;
  /**
   * Flags locales whose legally-sensitive copy (the `legal` namespace — law-2027,
   * CIAO@work, GDPR; §7.3) still needs a NATIVE legal/compliance review. This can
   * be true even for a `live` locale: `nl` shipped on its prefix in E4.1–E4.2, but
   * Story 4.7 machine-translated its `legal.json` and that copy is publicly visible
   * (G2) BEFORE a Belgian legal reviewer has signed off. The flag is metadata for
   * the Story 4.10 translation tracker; it gates nothing in code today.
   */
  needsLegalReview?: boolean;
}

/**
 * `en` (live, international) + `nl` (live, belgium) shipped in E4.1; `fr-be`
 * (live, belgium) was translated end-to-end and flipped `draft→live` in Story
 * 4.10 (E4.5) — the worked first run of the per-language rollout pipeline. A new
 * language still enters here as `status:"draft"` and flips to `"live"` only after
 * its catalogs pass the i18n gate (G2/G6); the matrix/hreflang/sitemap/switcher
 * auto-expand off `LIVE_LOCALES`.
 *
 * French spans BOTH markets, so it is region-suffixed (§1 escape-hatch): `fr-be` is
 * Belgian French (belgium market → gets law-2027 / CIAO@work). Generic/international
 * French (France etc.) would be a separate `fr` / `fr-fr` entry with market
 * "international" — added if/when that market is in scope. A bare `fr` is intentionally
 * NOT used, so the code is never ambiguous about which market it serves.
 */
export const LOCALES: LocaleDef[] = [
  { code: "en", htmlLang: "en", market: "international", status: "live", name: "English" },
  { code: "nl", htmlLang: "nl", market: "belgium", status: "live", name: "Nederlands", needsLegalReview: true },
  { code: "fr-be", htmlLang: "fr-BE", market: "belgium", status: "live", name: "Français (Belgique)", needsLegalReview: true },
  // Belgian English (belgium market → gets law-2027 / CIAO@work), mirroring the
  // fr-be region-suffix rule: bare `en` is the International locale, so Belgian
  // English is `en-be`. LIVE with NO own content dir: every namespace resolves via
  // `fallbackLng: SOURCE_LOCALE` ("en"), which carries the full English source
  // INCLUDING the belgium-only `legal` copy (law-2027 / CIAO@work), so the market
  // ships a complete English site. `needsLegalReview` stays set — a native
  // Belgian-English legal pass is still outstanding (metadata only, gates nothing).
  // The CMS `be` market may later author en-be catalogs to overlay this fallback.
  { code: "en-be", htmlLang: "en-BE", market: "belgium", status: "live", name: "English (Belgium)", needsLegalReview: true },
  // First INTERNATIONAL rollout language after the fr-be worked example (Story 4.10
  // follow-up). International market → the 9-industry + 4-post intl set, NO law-2027.
  // No needsLegalReview: the legal namespace is translated for key-parity but the
  // belgium-only legal pages are never served to this market. Appended AFTER `en` so
  // `liveLocaleForMarket("international")` keeps resolving to `en`.
  { code: "es", htmlLang: "es", market: "international", status: "live", name: "Español" },
  // International rollout batch (Story 4.10 follow-up). All market:international →
  // the 9-industry + 4-post intl set, NO law-2027, no needsLegalReview. Appended
  // AFTER `en` so `liveLocaleForMarket("international")` keeps resolving to `en`.
  { code: "de", htmlLang: "de", market: "international", status: "live", name: "Deutsch" },
  { code: "it", htmlLang: "it", market: "international", status: "live", name: "Italiano" },
  { code: "pt", htmlLang: "pt", market: "international", status: "live", name: "Português" },
  { code: "pl", htmlLang: "pl", market: "international", status: "live", name: "Polski" },
  { code: "ro", htmlLang: "ro", market: "international", status: "live", name: "Română" },
  { code: "no", htmlLang: "no", market: "international", status: "live", name: "Norsk" },
  { code: "bg", htmlLang: "bg", market: "international", status: "live", name: "Български" },
  { code: "tr", htmlLang: "tr", market: "international", status: "live", name: "Türkçe" },
  { code: "lt", htmlLang: "lt", market: "international", status: "live", name: "Lietuvių" },
];

/** x-default / fallback locale for unprefixed legacy paths (server.js 301s here). */
export const DEFAULT_LOCALE = "nl";

/** The locale UI strings are authored in (translation source of truth). */
export const SOURCE_LOCALE = "en";

/** Locales actually routed + prerendered today (derived — never hand-maintain a parallel list). */
export const LIVE_LOCALES: LocaleDef[] = LOCALES.filter((l) => l.status === "live");

/** Pure helper: is `code` one of the live, routed locales? */
export const isLiveLocale = (code?: string | null): boolean =>
  !!code && LIVE_LOCALES.some((l) => l.code === code);

/** Pure helper: look up a live locale definition by code (undefined if unknown/draft). */
export const liveLocale = (code?: string | null): LocaleDef | undefined =>
  code ? LIVE_LOCALES.find((l) => l.code === code) : undefined;

/**
 * Representative live locale code for a market (belgium → nl, international → en).
 * Switching market IS a URL change under prefix-all, so the market switchers
 * navigate to this locale via `useSwitchLocale()`. Falls back to the first live
 * locale if a market has none routed yet. Pure (NFR10).
 */
export const liveLocaleForMarket = (m: Market): string =>
  (LIVE_LOCALES.find((l) => l.market === m) ?? LIVE_LOCALES[0]).code;
